import React, { useState, useEffect, useContext } from 'react';
import { PubNubProvider } from 'pubnub-react';
import { useIntl } from 'react-intl';
import { Grid, styled, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { ArrowBack } from '@material-ui/icons';
import { Social } from './Social';
import { theme } from 'theme';

import { getUUID, useChat } from 'utils/useChat';
import { useDataHelper } from 'utils/dataHelper';
import { SdmTextButton } from 'components';
import translations from 'utils/translations';
import { LoaderContext } from 'context/LoaderContextProvider';
import { SizeContext } from 'context/SizeContextProvider';
import InvitedContainer from './InvitedContainer';
import { usePubnabLambdaServices } from 'utils/pubnubLambdaHelper';
import { useBreakoutRoomGql } from 'entities/breakoutRoom/breakoutRoomGql';
import { backHomeLink } from 'utils/constants';
import { isIOS } from 'react-device-detect';

const HamburgerIcon = styled('div')(({ open }) => ({
  width: 24,
  height: 14,
  position: 'relative',
  margin: 'auto 5px',
  WebkitTransform: 'rotate(0deg)',
  transform: 'rotate(0deg)',
  WebkitTransition: '.5s ease-in-out',
  MozTransition: '.5s ease-in-out',
  transition: '.5s ease-in-out',
  cursor: 'pointer',
  '& span': {
    backgroundColor: 'black',
    display: 'block',
    position: 'absolute',
    height: 2,
    width: '100%',
    borderRadius: 9,
    opacity: 1,
    left: 0,
    WebkitTransform: 'rotate(0deg)',
    transform: 'rotate(0deg)',
    WebkitTransition: '.25s ease-in-out',
    MozTransition: '.25s ease-in-out',
    transition: '.25s ease-in-out',
  },
  '& span:nth-child(1)': {
    top: open ? '-1px' : 0,
    left: 0,
    WebkitTransformOrigin: 'left center',
    MozTransformOrigin: 'left center',
    transformOrigin: 'left center',
    WebkitTransform: open ? 'rotate(45deg)' : 'rotate(0deg)',
    transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
  },
  '& span:nth-child(2)': {
    top: '50%',
    WebkitTransformOrigin: 'left center',
    MozTransformOrigin: 'left center',
    transformOrigin: 'left center',
    width: open ? '0%' : '100%',
    opacity: open ? 0 : 1,
  },
  '& span:nth-child(3)': {
    top: open ? 16 : '100%',
    left: 0,
    WebkitTransformOrigin: 'left center',
    MozTransformOrigin: 'left center',
    transformOrigin: 'left center',
    WebkitTransform: open ? 'rotate(-45deg)' : 'rotate(0deg)',
    transform: open ? 'rotate(-45deg)' : 'rotate(0deg)',
  },
}));

const ChatSession = ({ locale, loggedUser, session, isModerator, isVip }) => {
  const channel = getUUID(session);
  const breakoutRoomGql = useBreakoutRoomGql();

  const getUserMetadata = () => {
    //TODO: modificare la chiamata a gql per recuperare isVip da invitationInfo
    setUserMetadata({
      ...loggedUser,
      givenName: isModerator ? loggedUser.name : loggedUser.givenName,
      familyName: isModerator ? '' : loggedUser.familyName,
      isModerator: isModerator,
      isVip: isVip,
    });
    fetchChatInfo();
  };

  const fetchChatInfo = async () => {
    const chat = await breakoutRoomGql.getBrInfo(session);
    if (chat) setChatInfo(chat);
  };

  //States
  const [userMetadata, setUserMetadata] = useState();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [chatInfo, setChatInfo] = useState();

  //Contexts
  const { setLoaderActive } = useContext(LoaderContext);
  const { getInnerHeight } = useContext(SizeContext);

  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  //Hooks
  const intl = useIntl();
  const i18n = translations.chatPage(intl);
  const dataHelper = useDataHelper();
  const {
    client,
    messages,
    occupants,
    isSubscribed,
    hasBeenBanned,
    publish,
    fetchOlderMessages,
    fetchUsersMetadata,
    logout,
  } = useChat({
    channel: channel,
    userUuid: loggedUser?.id,
    userMetadata: userMetadata,
  });
  const mediaTheme = useTheme();
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  const isXsDown = useMediaQuery(mediaTheme.breakpoints.down('xs'));

  //Effects
  useEffect(() => {
    if (session) getUserMetadata();
  }, [session]);

  useEffect(() => {
    showLoader();
    if (client && userMetadata && isSubscribed && chatInfo) hideLoader();
  }, [client, userMetadata, isSubscribed, chatInfo]);

  const getAccessToken = () => {
    return loggedUser?.id;
  };

  const toggleHamburger = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const moderatorService = usePubnabLambdaServices({
    accessToken: getAccessToken(),
    sessionId: session,
    channel: channel,
    language: locale,
  });

  const handleBan = async (id) => {
    const res = await moderatorService.kickUser(
      moderatorService.createAuthKey(id)
    );
    //console.log('🚀 ~ file: Social.js ~ line 92 ~ handleBan ~ res', res);
    return res;
  };

  const handleUnban = async (id) => {
    const res = await moderatorService.restoreUser(
      moderatorService.createAuthKey(id)
    );
    return res;
  };

  const handleDelete = async (messageTimetoken, msg) => {
    const res = await moderatorService.purgeMessage(messageTimetoken, msg);
    return res;
  };

  const handleBack = async () => {
    await logout();
    dataHelper.backToBr(userMetadata?.externalId);
  };

  return (
    <div
      style={{
        backgroundColor: '#F3F3F3',
        height: isIOS && isXsDown ? '100vh' : `${getInnerHeight()}px`,
      }}
    >
      {isSubscribed && client ? (
        <PubNubProvider client={client}>
          <Grid
            container
            style={{
              height: '100%',
              overflowX: 'hidden',
              ...(isIOS &&
                isXsDown && {
                  position: 'fixed',
                  bottom: 0,
                }),
              // marginTop: isModerator ? (isSmDown ? -39 : -78) : 0,
            }}
          >
            <Grid item md={8} sm={12}>
              <div
                style={{
                  height: 78,
                  width: isSmDown ? '100vw' : 'unset',
                  display: 'flex',
                  justifyContent: 'space-between',
                  backgroundColor: theme.colors.primary.white,
                  alignItems: 'center',
                  padding: isSmDown ? '0px 10px 0px 0px' : '0 30px',
                }}
              >
                <SdmTextButton
                  variant={'noneInverted'}
                  text={i18n.back}
                  onClick={handleBack}
                  style={{
                    top: 4,
                    left: 16,
                  }}
                >
                  <ArrowBack />
                </SdmTextButton>
                <img
                  src={'/themes/custom/sdm/images/header/logo-black.svg'}
                  alt="logo-sdm"
                  style={{
                    height: 34,
                    width: isSmDown ? 220 : 241,
                    objectFit: 'contain',
                    marginRight: isSmDown ? 15 : 0,
                  }}
                  onClick={() => (window.location.href = backHomeLink)}
                />
                {isSmDown ? (
                  <HamburgerIcon
                    onClick={() => toggleHamburger()}
                    open={isMenuOpen}
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                  </HamburgerIcon>
                ) : null}
              </div>
              <Social
                i18n={i18n}
                messages={messages}
                publish={publish}
                hasBeenBanned={hasBeenBanned}
                hasChatExpired={
                  !isModerator && dataHelper.checkPassedDate(chatInfo?.end)
                }
                isModerator={userMetadata.isModerator}
                lang={locale}
                handleDelete={handleDelete}
                fetchOlderMessages={fetchOlderMessages}
                isXsDown={isXsDown}
                isVip={userMetadata.isVip}
                isMenuOpen={isMenuOpen}
              />
            </Grid>
            <Grid
              item
              md={4}
              sm={12}
              style={{
                // display: isXsDown ? (isMenuOpen ? 'block' : 'none') : 'block',
                position: isSmDown ? 'fixed' : 'inherit',
                transition: '.5s ease-in-out',
                left: isMenuOpen ? 0 : '100%',
                ...(!!isSmDown && {
                  top: 77,
                }),
                ...(!!isMenuOpen && {
                  top: 77,
                  height: '100%',
                }),
                width: '100%',
                backgroundColor: '#626262',
                overflowY: 'auto',
              }}
            >
              <InvitedContainer
                i18n={i18n.usersTab}
                occupants={occupants}
                session={session}
                channel={channel}
                userMetadata={userMetadata}
                chatInfo={chatInfo}
                isModerator={userMetadata.isModerator}
                handleBan={handleBan}
                handleUnban={handleUnban}
                hasBeenBanned={hasBeenBanned}
                fetchUsersMetadata={fetchUsersMetadata}
                isSmDown={isSmDown}
              />
            </Grid>
          </Grid>
        </PubNubProvider>
      ) : null}
    </div>
  );
};

export default ChatSession;
